import { isTrackingMode, type TrackingMode } from "@/lib/utils/trackingMode";

/**
 * Which sentence, if any, the sent-mail details view owes the reader about
 * tracking — as an i18n key in the `pEmailSent` namespace, or `null`.
 *
 * An event list with no OPEN in it means one of two very different things: the
 * guest did not open the mail, or opens were never recorded for it. Without
 * this the two look identical, and the second reads as a disengaged guest.
 *
 *  - `trackingDegradedHint` — the template asked for `clicks` but the mail went
 *    out as `full` (main-consumer's override flag was off). Checked FIRST: it
 *    is the one case where what the organiser chose is not what was sent.
 *  - `trackingClicksHint`   — sent without an open pixel.
 *  - `trackingNoneHint`     — sent with no tracking at all.
 *  - `null`                 — `full`, or a row from before 2026-09 (no field):
 *                             today's behaviour, nothing to explain.
 */
export type TrackingNoticeKey =
  | "trackingDegradedHint"
  | "trackingClicksHint"
  | "trackingNoneHint";

export function trackingModeNotice(row: {
  trackingMode?: unknown;
  trackingModeRequested?: unknown;
} | null | undefined): TrackingNoticeKey | null {
  const sent: TrackingMode | null = isTrackingMode(row?.trackingMode) ? row.trackingMode : null;
  const requested = isTrackingMode(row?.trackingModeRequested) ? row.trackingModeRequested : null;
  // `clicks` asked, something else sent: the consumer's override flag was off.
  // The OTHER degradation — `none` asked, `full` sent, because no notrack set
  // was configured — gets no sentence: it cannot happen on staging or
  // production (both configure the twins), the row then says `full`, and the
  // event list underneath shows the opens. Saying nothing is accurate there.
  if (requested === "clicks" && sent !== "clicks") return "trackingDegradedHint";
  if (sent === "clicks") return "trackingClicksHint";
  if (sent === "none") return "trackingNoneHint";
  return null;
}
